// ./src/hooks/useForms.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  Form,
  FormService,
  FormFilter,
  FormInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseFormsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<FormFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseFormsReturn {
  // Data state
  form: Form | null;
  forms: Form[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<FormFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingFormId: string | null;
  selectedForm: Form | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<FormFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Form[]>;
  create: (params: FormInput) => Promise<Form | null>;
  update: (id: string, params: FormInput) => Promise<Form | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<FormFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedForm: (form: Form | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useForms = (options: UseFormsOptions = {}): UseFormsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = { status: [STATUSES.ACTIVE] },
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [form, setForm] = useState<Form | null>(null);
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<FormFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingFormId, setRemovingFormId] = useState<string | null>(null);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => forms.length === 0 && !loading,
    [forms.length, loading]
  );

  // Helper function to update form in list if it exists, or add it if not
  const updateOrAddFormInList = useCallback((updatedForm: Form) => {
    setForms((prev) => {
      const existingIndex = prev.findIndex((f) => f.id === updatedForm.id);
      if (existingIndex >= 0) {
        const newForms = [...prev];
        newForms[existingIndex] = updatedForm;
        return newForms;
      } else {
        return [updatedForm, ...prev];
      }
    });
  }, []);

  // Helper function to remove form from list if it exists
  const removeFormFromList = useCallback((formId: string) => {
    setForms((prev) => prev.filter((f) => f.id !== formId));
  }, []);

  // Helper function to remove multiple forms from list
  const removeFormsFromList = useCallback((formIds: string[]) => {
    const idsSet = new Set(formIds);
    setForms((prev) => prev.filter((f) => !idsSet.has(f.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setForm(null);
    setForms([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingFormId(null);
    setSelectedForm(null);
  }, []);

  // Get single form (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await FormService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const formData = result.data[0] as Form;
          setForm(formData);
          updateOrAddFormInList(formData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load form";
          setLastError(errorMessage);
          setForm(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load form";
        setLastError(errorMessage);
        setForm(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFormInList]
  );

  // List forms (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<FormFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setForms([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await FormService.list(
          filter as FormFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newForms = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setForms((prev) => {
              const existingIds = new Set(prev.map((f) => f.id));
              const uniqueNewForms = newForms.filter(
                (f: any) => !existingIds.has(f.id)
              );
              return [...prev, ...uniqueNewForms];
            });
          } else {
            setForms(newForms);
          }

          setHasMore(newForms.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load forms";
          setLastError(errorMessage);
          if (!isAppending) setForms([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load forms";
        setLastError(errorMessage);
        if (!isAppending) setForms([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many forms by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Form[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await FormService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedForms = Array.isArray(result.data) ? result.data : [];
          loadedForms.forEach((form: any) => {
            updateOrAddFormInList(form);
          });
          setLastError(null);
          return loadedForms;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load forms";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load forms";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFormInList]
  );

  // Create form (matching API method name)
  const create = useCallback(
    async (params: FormInput): Promise<Form | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await FormService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newForm = result.data[0] as Form;
          setForm(newForm);
          setForms((prev) => [newForm, ...prev]);
          setLastError(null);
          return newForm;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create form";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create form";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update form (matching API method name)
  const update = useCallback(
    async (id: string, params: FormInput): Promise<Form | null> => {
      if (!id) {
        setLastError("Form ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await FormService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedForm = result.data[0] as Form;
          setForm(updatedForm);
          updateOrAddFormInList(updatedForm);
          setLastError(null);
          return updatedForm;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update form";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update form";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddFormInList]
  );

  // Remove form (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Form ID is required for removal");
        return false;
      }

      setRemovingFormId(id);
      setLastError(null);

      try {
        const result = await FormService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setForm(null);
          removeFormFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove form";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove form";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingFormId(null);
      }
    },
    [removeFormFromList]
  );

  // Remove many forms (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one form ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await FormService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setForm(null);
          removeFormsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove forms";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove forms";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeFormsFromList]
  );

  // Load more forms
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh forms
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    form,
    forms,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingFormId,
    selectedForm,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedForm,
    reset,
    lastError,
    clearError,
  };
};
